/**
 * MessageTaskReferences 组件
 * 显示消息中提到的任务引用，复用 TaskList 组件
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronUp, FiList } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

import { TempoTaskReference } from '../../../../constants/tempoProtocol';
import { tasksAtom } from '../../../../atoms/v0/taskAtoms';
import { Task } from '../../../../types/task';
import { TaskItem } from '../tasks/TaskItem';

interface MessageTaskReferencesProps {
  taskReferences: TempoTaskReference[];
  onToggleTask: (taskId: string, completed: boolean) => void;
  onTaskClick: (taskId: string) => void;
}

/**
 * 创建已删除任务的占位符对象
 */
const createDeletedTaskPlaceholder = (reference: TempoTaskReference): Task => ({
  id: reference.taskId,
  userId: 'deleted',
  title: reference.displayText,
  notes: '',
  done: false,
  vital: false,
  urgent: false,
  fixed: false,
  embedding: null,
  createdAt: '',
  updatedAt: '',
  // 标记为已删除，用于特殊样式处理
  _isDeleted: true
} as Task & { _isDeleted: boolean });

/**
 * 消息任务引用组件
 */
export const MessageTaskReferences: React.FC<MessageTaskReferencesProps> = ({
  taskReferences,
  onToggleTask,
  onTaskClick
}) => {
  const { t } = useTranslation(['v3/chat']);
  const [tasks] = useAtom(tasksAtom);
  const [isExpanded, setIsExpanded] = useState(true);

  // 获取实际的任务对象，包括已删除任务的占位符
  const referencedTasks = useMemo(() => {
    return taskReferences.map(reference => {
      const existingTask = tasks.find(task => task.id === reference.taskId);

      if (existingTask) {
        return existingTask;
      } else {
        // 创建已删除任务的占位符
        return createDeletedTaskPlaceholder(reference);
      }
    });
  }, [taskReferences, tasks]);

  // 如果没有任务引用，不显示组件
  if (taskReferences.length === 0) {
    return null;
  }

  // 处理已删除任务的特殊逻辑
  const handleDeletedTaskToggle = useCallback((taskId: string, completed: boolean) => {
    // 已删除任务不允许状态切换
    return;
  }, []);

  const handleDeletedTaskClick = useCallback((taskId: string) => {
    // 已删除任务不允许点击
    return;
  }, []);

  return (
    <div className="mt-2">
      {/* 折叠/展开按钮 */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center text-xs text-ios-gray dark:text-ios-dark-gray hover:text-ios-blue dark:hover:text-ios-dark-blue transition-colors mb-2"
      >
        {isExpanded ? (
          <><FiChevronUp className="mr-1" />{t('v3/chat:messageTaskReferences.hide', '隐藏提到的任务')}</>
        ) : (
          <><FiChevronDown className="mr-1" />{t('v3/chat:messageTaskReferences.show', '显示提到的任务')} ({taskReferences.length})</>
        )}
      </button>

      {/* 任务列表 */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="bg-white dark:bg-ios-dark-gray5 rounded-lg overflow-hidden">
              {/* 标题 */}
              <div className="flex items-center px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
                <FiList className="text-ios-blue dark:text-ios-dark-blue mr-2" />
                <span className="text-ios-body font-medium text-ios-blue dark:text-ios-dark-blue">
                  {t('v3/chat:messageTaskReferences.title', '提到的任务')}
                </span>
                <span className="ml-2 text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  ({referencedTasks.length})
                </span>
              </div>

              {/* 任务列表 */}
              <div className="relative">
                {referencedTasks.map((task, index) => {
                  const isDeleted = (task as any)._isDeleted;
                  const isLast = index === referencedTasks.length - 1;

                  if (isDeleted) {
                    // 已删除任务的特殊处理
                    return (
                      <div
                        key={task.id}
                        className="relative opacity-60"
                      >
                        <div className="pointer-events-none">
                          <TaskItem
                            task={task}
                            onToggle={handleDeletedTaskToggle}
                            onClick={handleDeletedTaskClick}
                            isLast={isLast}
                            hideProjectName={true}
                            context="chat"
                          />
                        </div>
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-ios-gray dark:text-ios-dark-gray bg-white dark:bg-ios-dark-gray5 px-1 rounded pointer-events-none">
                          {t('v3/chat:messageTaskReferences.deleted', '已删除')}
                        </div>
                      </div>
                    );
                  }

                  // 正常任务
                  return (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onToggle={onToggleTask}
                      onClick={onTaskClick}
                      isLast={isLast}
                      hideProjectName={true}
                      context="chat"
                    />
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageTaskReferences;
